import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, Tab, Tabs, Tooltip } from "@mui/material";
import ClientMeetings from "../Client/ClientMeetings";
import ClientActionItem from "../Client/ClientActionItem";
import SwitchComponent from "../Client/SwitchComponent";
import useGlobalContext from "../../context/useGlobalContext";
import "../../styles/clientPage.css";
import CustomDatePicker from "../CustomDatePicker/CustomDatePicker";
import { capitalizeFirstLetter, convertToUTC, isDateRange } from "../../utils/utils";
import { format } from "date-fns";
import prepShare from "../../assets/client/prepShare.svg";
import { useLocation, useNavigate } from "react-router-dom";
import ClientFingertipMetrics from "./ClientFingertipMetrics";
import ClientService from "../../services/clients/ClientService";
import { AccountViewState, InteractionCounts } from "../../utils/types";
import ClientAccountCases from "./ClientAccountCases";
import ThemeSection from "./Themes/ThemeSection";
import { ThemeCategoryKey } from "../../interfaces/themes";
import Opportunities from "../Opportunities/Opportunities";
import SalesforceIcon from "../../assets/accounts/salesforce-icon.svg";

interface ClientInteractionActionViewProps {
  clientDomain: string;
  subOrgId: string;
  isSlideOverOpen?: boolean;
  selectedNps?: string | null;
  removeCustomDateRange?: boolean;
  slideOverParent?: string;
  activeStat?: string | null;
  userDetails?: {
    userEmail: string;
    userProfileOrgId?: string;
  };
  selectedUser?: any;
  parentPageIdentifier?: string;
  relationshipType?: string;
  appliedFilters?: Record<string, string>;
  customDateRange?: { startDate: string; endDate: string } | string;
  selectedTab?: number;
  clickeduserEmail?: string;
  userName?: string;
  clientOrgId?: string;
  initialTab?: TabKey;
  initialThemeCategory?: ThemeCategoryKey;
  clientName?: string;
  isSpaceMode?: boolean;
  spaceId?: string[];
  accountViewState?: AccountViewState;
  salesforceAccountUrl?: string;
  salesforceAccountId?: string;
}


type TabKey =
  | "fingertipMetrics"
  | "interactions"
  | "themes"
  | "actionItems"
  | "cases"
  | "opportunity";

const ClientInteractionActionView: React.FC<ClientInteractionActionViewProps> = ({
  clientDomain, subOrgId,isSlideOverOpen, selectedNps, removeCustomDateRange, slideOverParent,
   activeStat, userDetails, selectedUser, parentPageIdentifier, relationshipType, appliedFilters, customDateRange,
    selectedTab, clickeduserEmail, userName, clientOrgId, initialTab, initialThemeCategory, clientName, isSpaceMode, spaceId, accountViewState, salesforceAccountUrl, salesforceAccountId
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isRated, setIsRated] = useState(true);
  const [counts, setCounts] = useState<InteractionCounts>({ rated: 0, total: 0, upcomingInteractionId: null });
  const [upcomingInteractionId, setUpcomingInteractionId] = useState<string | null>(null);
  const autoDecidedRef = useRef(false);
  const [userToggled, setUserToggled] = useState(false);
  const [opportunityHeaderActions, setOpportunityHeaderActions] = useState<React.ReactNode>(null);

  const { startEndMeetingTime, accountDetails, dateRange } = useGlobalContext();
  const { featureMap, impersonationDetails } = useGlobalContext();
  const isPrepNotesEnabled = featureMap?.prep_notes === true;
  const isFingertipMetricsEnabled = featureMap?.client_fingertip_metrics === true;
  const isDefaultInteractionsTabEnabled = featureMap?.accounts_slideover_default_interactions_tab === true;
  const isAccountsThemesTabEnabled = featureMap?.accounts_themes_tab === true;
  const loggedInProfileOrgId = accountDetails?.profileOrgId ?? "";
  const impersonatingUserPorId = impersonationDetails?.impersonatingUserPorId;
  const accessLevel = impersonationDetails?.accessType;
  const isImpersonating = impersonationDetails?.isImpersonating === true;
  const formattedEndDate = isDateRange(customDateRange)
    ? format(new Date(customDateRange.endDate), "MMM dd yyyy")
    : null;
  const effectiveInitialTab =
   initialTab || location.state?.initialTab;
  const effectiveAccountViewState: AccountViewState = {
    ...(accountViewState ?? {
      selectedView: isSpaceMode ? "spaces" : "org",
      selectedSpaces: spaceId ?? [],
      selectedUserPorOrgId: userDetails?.userProfileOrgId ?? null,
      selectedAccountRelationshipType: relationshipType,
      clientDomain,
      clientName,
      clientOrgId,
      subOrgId,
      slideOverParent,
      parentPageIdentifier,
    }),
    salesforceAccountId: accountViewState?.salesforceAccountId || salesforceAccountId,
    salesforceAccountUrl: accountViewState?.salesforceAccountUrl || salesforceAccountUrl,
  };

  const isThemesTabAllowed =
    isAccountsThemesTabEnabled &&
    slideOverParent === "account-parent-page" &&
    parentPageIdentifier === "account-customer";
  const isOpportunityTabEnabled =
    featureMap?.opportunities_slideOver === true &&
    slideOverParent === "account-parent-page" &&
    (parentPageIdentifier === "account-customer" ||
    parentPageIdentifier === "account-spaces") &&
    Boolean(subOrgId);

  const getDefaultTab = (): TabKey => {
    if (effectiveInitialTab === "interactions") return "interactions";
    if (effectiveInitialTab === "themes" && isThemesTabAllowed) return "themes";
    if (effectiveInitialTab === "actionItems") return "actionItems";
    if (effectiveInitialTab === "opportunity" && isOpportunityTabEnabled) return "opportunity";
    if (effectiveInitialTab === "fingertipMetrics" && isFingertipMetricsEnabled) {
      return "fingertipMetrics";
    }
    if (isDefaultInteractionsTabEnabled) {
      return "interactions";
    }
    return isFingertipMetricsEnabled ? "fingertipMetrics" : "interactions";
  };

  const [tabValue, setTabValue] = useState<TabKey>(getDefaultTab);
  const effectiveUpcomingInteractionId = upcomingInteractionId || counts.upcomingInteractionId;

  const handleTabChange = (_event: React.SyntheticEvent, newValue: TabKey) => {
    setTabValue(newValue);
  };

  const handleToggleChange = (
    _event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    setIsRated(checked);
    setUserToggled(true);
  };

  const {
    data: upcomingInteractionData,
    isSuccess: isUpcomingInteractionSuccess,
  } = ClientService.useGetClientInteractions(
    {
      startTime: convertToUTC(startEndMeetingTime.start_time),
      endTime: convertToUTC(startEndMeetingTime.end_time),
      employeePorId: userDetails?.userProfileOrgId || loggedInProfileOrgId,
      limit: 1,
      offset: 1,
      relationshipType: relationshipType || undefined,
      sortBy: "",
      sortOrder: "",
      orgId: clientOrgId,
      subOrgId: subOrgId,
      impersonatingPorId: impersonatingUserPorId,
      accessLevel: accessLevel,
      ...(parentPageIdentifier === "account-customer" ||
                parentPageIdentifier === "account-spaces") && {
                isForOrg: true,
            },
    },
    Boolean(isPrepNotesEnabled && isSlideOverOpen)
  );

  useEffect(() => {
    if (upcomingInteractionData?.upcomingInteractionId && isUpcomingInteractionSuccess) {
      setUpcomingInteractionId(
        upcomingInteractionData.upcomingInteractionId
      );
    }
  }, [upcomingInteractionData, isUpcomingInteractionSuccess, isSlideOverOpen]);
  
  useEffect(() => {
    if (userToggled) return;
    if (autoDecidedRef.current) return;
  
    // wait until we actually have data
    if (counts.rated === 0 && counts.total === 0) return;
  
    if (counts.rated > 0) {
      // Rated has priority
      setIsRated(true);
      autoDecidedRef.current = true;
      return;
    }
  
    if (counts.rated === 0 && counts.total > 0) {
      setIsRated(false);
      autoDecidedRef.current = true;
    }
  }, [counts, userToggled]);

  useEffect(() => {
    // When slideover closes → reset tab
    if (!isSlideOverOpen) {
      setTabValue(getDefaultTab());
      setUpcomingInteractionId(null);
      setCounts(prev => ({
        ...prev,
        upcomingInteractionId: null,
      }));
    }
  }, [isSlideOverOpen, isFingertipMetricsEnabled, isDefaultInteractionsTabEnabled, initialTab]);  

  useEffect(() => {
    if (!isSlideOverOpen || !isFingertipMetricsEnabled) return;
    if (!location.state?.restoreTooltipInSlideOver) return;
    if (!location.state?.interactionId) return;

    const isOpportunityRestore = effectiveInitialTab === "opportunity";

    if (isOpportunityRestore) {
      if (isOpportunityTabEnabled) {
        setTabValue("opportunity");
      }
      return;
    }

    if (isFingertipMetricsEnabled) {
      setTabValue("fingertipMetrics");
    }
  }, [
    isSlideOverOpen,
    isFingertipMetricsEnabled,
    isOpportunityTabEnabled,
    effectiveInitialTab,
    location.key,
    location.state,
  ]);
  useEffect(() => {
    autoDecidedRef.current = false;
    setUserToggled(false);
    setIsRated(true); // always start with Rated
  }, [clientDomain, isSlideOverOpen]);  

  useEffect(() => {
    if (!isFingertipMetricsEnabled && tabValue === "fingertipMetrics") {
      setTabValue("interactions");
    }
  }, [isFingertipMetricsEnabled, tabValue]);

  useEffect(() => {
  if (!isThemesTabAllowed && tabValue === "themes") {
    setTabValue(getDefaultTab());
  }
}, [isThemesTabAllowed, tabValue]);

  useEffect(() => {
    setTabValue(getDefaultTab());
  }, [effectiveInitialTab, isFingertipMetricsEnabled, isDefaultInteractionsTabEnabled, clientDomain]);

  const shouldRenderDatePicker =
    tabValue !== "fingertipMetrics" &&
    slideOverParent !== "client-stats" &&
    slideOverParent !== "client-interaction-overview";


  return (
    <>
    <Box className="client-interaction__header">
      <Typography className="client-interaction__title">
        {
          <>
            {capitalizeFirstLetter(clientName || "")}
            {(isPrepNotesEnabled) && (relationshipType === "client" || relationshipType === "prospect") && !isImpersonating &&(
              <Tooltip
                title={
                  !effectiveUpcomingInteractionId
                    ? "No upcoming interactions in the next 3 days"
                    : ""
                }
                arrow
                placement="top"
              >
                <span
                  className="prep-title"
                  style={{
                    opacity: effectiveUpcomingInteractionId ? 1 : 0.3,
                    transition: "opacity 0.3s ease-in-out",
                  }}
                  onClick={() =>
                    effectiveUpcomingInteractionId &&
                    navigate(`/meeting/${effectiveUpcomingInteractionId}`, {
                      state: { selectedTab: "private-notes" },
                    })
                  }
                >
                  &nbsp;|&nbsp;&nbsp;Prep for the Interaction
                  <img src={prepShare} alt="share" className="prep-icon" />
                </span>
              </Tooltip>
            )}
            {isOpportunityTabEnabled && salesforceAccountUrl && (
              <span className="prep-title">
                &nbsp;|&nbsp;&nbsp;
                <Tooltip title="Open in Salesforce" arrow placement="top">
                <a
                  href={salesforceAccountUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="salesforce-link"
                >
                  <img src={SalesforceIcon} alt="Salesforce" className="salesforce-icon" />
                </a>
                </Tooltip>
              </span>
            )}
          </>
        }
      </Typography>
      {(slideOverParent === "client-stats" || slideOverParent === "client-interaction-overview") && formattedEndDate && (
        <span
          className="client-business__daterange__label"
          style={{ marginLeft: "auto" }} // pushes it to the far right
        >
          <h1 style={{ fontSize: "16px", margin: 0, fontWeight: 500 }}>
            Week ending{" "}
            <span className="client-business__daterange__date">
              {formattedEndDate}
            </span>
          </h1>
        </span>
      )}
      {shouldRenderDatePicker && (
        <Box sx={{ display: tabValue === "opportunity" ? "none" : "block" }}>
          <CustomDatePicker 
            disabled={slideOverParent === "client-health" 
              || slideOverParent === "exec-attention"
              || slideOverParent === "analytics-slideover"}
            {...(!removeCustomDateRange && { customDateRange })}
            isSlideOverOpen={isSlideOverOpen}
          />
        </Box>
      )}
    </Box>
    <Box className="client-interaction">
      {/* Header */}

      <Box className="client-interaction__tabs-container">
        <Tabs value={tabValue} onChange={handleTabChange}>
          {/* Conditional tabs */}
          {isFingertipMetricsEnabled && (
            <Tab label="Fingertip Metrics" value="fingertipMetrics" />
          )}
          {slideOverParent === "account-parent-page" && clientDomain?.toLowerCase() === "karat.com" && <Tab label="Past-To-Present" value="prospectP2P" />}
          {slideOverParent === "account-parent-page" && clientDomain?.toLowerCase() === "karat.com" && <Tab label="Contacts" value="contacts" />}
          <Tab label="Interactions" value="interactions" />
          {isThemesTabAllowed && <Tab label="Themes" value="themes" />}
          {!isImpersonating && <Tab label="Tasks" value="actionItems" /> }
          {slideOverParent === "account-parent-page" && (clientName?.toLowerCase() === "eisneramper llp - usa" || clientName?.toLowerCase() === "highradius") &&<Tab label="Cases" value="cases"/> }
          {isOpportunityTabEnabled && <Tab label="Opportunity" value="opportunity" /> }
        </Tabs>

        {tabValue === "opportunity" && opportunityHeaderActions && (
          <Box sx={{ display: "flex", alignItems: "center", mb: "6px" }}>
            {opportunityHeaderActions}
          </Box>
        )}

        <Box
          className={`client-interaction__switch ${
            tabValue !== "interactions" ? "client-interaction__switch--hidden" : ""
          }`}
          style={tabValue === "opportunity" ? { display: "none" } : undefined}
        >
          <Box className="client-interaction__switch-inner">
            <SwitchComponent
              rated={isRated}
              firstText="All"
              secondText="Rated"
              onToggleChange={handleToggleChange}
              ratedCount={counts.rated}
              totalCount={counts.total}
              hideUnderline={true}
            />
          </Box>
        </Box>
      </Box>

      {/* Tab Content */}
      <Box className="client-interaction__content">
        {tabValue === "fingertipMetrics" && (
          <ClientFingertipMetrics
            clientOrgId={clientOrgId} 
            subOrgId={subOrgId}
            isSlideOverOpen={isSlideOverOpen}
            clientName={clientName}
            tooltipNavigationState={{
              parent_page_identifier: parentPageIdentifier,
              isSlideOverOpen,
              slideOverParent,
              activeStat,
              clientDomain,
              orgId: clientOrgId,
              subOrgId,
              appliedFilters,
              customDateRange,
              userDetails,
              selectedNps,
              clickeduserEmail,
              clientName,
              relationshipType,
              accountViewState: effectiveAccountViewState,
            }}
          />
        )}
        {tabValue === "interactions" && (
          <ClientMeetings
            clientDomain={clientDomain}
            startTime={startEndMeetingTime.start_time}
            endTime={startEndMeetingTime.end_time}
            isRated={isRated}
            setCounts={setCounts}
            parent_page_identifier={parentPageIdentifier}
            isSlideOverOpen = {isSlideOverOpen}
            selectedNps = {selectedNps}
            slideOverParent={slideOverParent}
            activeStat = {activeStat}
            userDetails={userDetails}
            selectedUser = {selectedUser}
            relationshipType={relationshipType}
            appliedFilters={appliedFilters}
            customDateRange={customDateRange}
            selectedTab={selectedTab}
            clickeduserEmail={clickeduserEmail}
            userName={userName}
            orgId={clientOrgId}
            subOrgId={subOrgId}
            clientName={clientName}
            isSpaceMode={isSpaceMode}
            activeView={tabValue}
            accountViewState={effectiveAccountViewState}
          />
        )}
        {tabValue === "themes" && isThemesTabAllowed && (
          <ThemeSection
            startTime={convertToUTC(startEndMeetingTime.start_time)}
            endTime={convertToUTC(startEndMeetingTime.end_time)}
            subOrgId={subOrgId}
            orgId={clientOrgId}
            clientName={clientName}
            activeView={tabValue}
            parentPageIdentifier={parentPageIdentifier}
            customDateRange={customDateRange}
            startEndMeetingTime={startEndMeetingTime}
            accountViewState={effectiveAccountViewState}
            isSlideOverOpen = {isSlideOverOpen}
            slideOverParent={slideOverParent}
            initialCategory={initialThemeCategory}
          />
        )}
        {tabValue === "actionItems" && !isImpersonating &&(
          <ClientActionItem clientDomain={clientDomain} clientOrgId={clientOrgId} subOrgId={subOrgId}/>
        )}
        {tabValue === "cases" && (
          <ClientAccountCases />
        )}
        {tabValue === "opportunity" && isOpportunityTabEnabled && (
          <Opportunities
            subOrgId={subOrgId}
            employeePorId={userDetails?.userProfileOrgId}
            isAccountSlideOver
            dateRange={
              dateRange?.startDate && dateRange?.endDate
                ? {
                    startDate: format(dateRange.startDate, "yyyy-MM-dd'T'00:00:00xxx"),
                    endDate: format(dateRange.endDate, "yyyy-MM-dd'T'23:59:59xxx"),
                  }
                : isDateRange(customDateRange)
                ? {
                    startDate: customDateRange.startDate,
                    endDate: customDateRange.endDate,
                  }
                : undefined
            }
            accountViewState={effectiveAccountViewState}
            onHeaderActionsChange={setOpportunityHeaderActions}
            sf_account_id={salesforceAccountId}
          />
        )}
      </Box>
    </Box>
    </>
  );
};

export default ClientInteractionActionView;
